import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isStaleChunkError, recoverFromStaleChunk } from './staleChunkRecovery';

interface Props {
  appName: string;
  /**
   * Optional retry hook. The parent should bump a remount key here so the
   * underlying lazy() is re-created — without that, React.lazy's cached
   * rejection makes the in-place reset a no-op.
   */
  onRetry?: () => void;
  children: ReactNode;
}

interface State {
  crashed: boolean;
  error?: Error;
}

export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { crashed: false };

  static getDerivedStateFromError(error: Error): State {
    return { crashed: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // A chunk that 404s is almost never a bug in the app — it is this tab still
    // running the build that was live before the last deploy. Reload onto the
    // current one rather than blaming the app in a crash screen the user can
    // only escape by hand. Rate-limited, so a genuinely broken deploy falls
    // through to the UI below instead of reloading forever.
    if (isStaleChunkError(error) && recoverFromStaleChunk(`${this.props.appName} chunk load`)) {
      return;
    }
    console.error(`[${this.props.appName}] crashed`, error, info);
  }

  private handleRetry = () => {
    this.setState({ crashed: false, error: undefined });
    this.props.onRetry?.();
  };

  private handleReload = () => {
    window.location.reload();
  };

  override render() {
    if (this.state.crashed) {
      const detail = this.state.error?.message;
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4 p-10 text-center">
          <p className="text-3xl">{this.props.appName} ran into a problem.</p>
          {detail && <p className="text-muted-foreground max-w-md break-words text-lg">{detail}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.handleRetry}
              className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-xl"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="border-border rounded-md border px-4 py-2 text-xl"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
